import { isSelectEntryEdited, isTextFieldEntryEdited, ListGroup } from '@bpmn-io/properties-panel';
import { useCallback, useMemo } from '@bpmn-io/properties-panel/preact/hooks';
import { getBusinessObject, is, isAny } from 'bpmn-js/lib/util/ModelUtil';
import { getExtensionElementsList } from 'camunda-bpmn-js/lib/util/ExtensionElementsUtil';
import { without } from 'min-dash';
import type { EntryDefinition, GroupFn, ListItemDefinition } from '../../../types/properties-panel';
import { useService } from '../hooks/useService';
import { useFetchSpSiteLists } from '../hooks/useFetchSpSiteLists';
import { createElement } from '../util/factory-util';
import { createOption, getOption, getOptionLabel, SelectEntry } from './SelectEntry';
import { TextAreaEntry } from './TextAreaEntry';
import { TextFieldEntry } from './TextFieldEntry';
import { h } from '@bpmn-io/properties-panel/preact';
import { useFetchActiveProcesses } from '../hooks/useFetchActiveProcesses';
import { useFetchSpSubSites } from '../hooks/useFetchSpSubSites';
import { TextWithFieldRef } from './TextWithFieldRef';

const groupId = 'spSiteListActionsGroup';

export const TaskSpGroup: GroupFn = (element, injector) => {
    // bpmn:Task includes all tasks - is it a feature or a bug
    if (
        !is(element, 'bpmn:UserTask') &&
        isAny(element, ['bpmn:Task', 'bpmn:ServiceTask', 'bpmn:ScriptTask' /*, 'bpmn:UserTask'*/])
    ) {
        // const moddle = injector.get('moddle'); // npm package moddle - https://github.com/bpmn-io/moddle/blob/main/lib/moddle.js
        const translate = injector.get('translate');

        return {
            add: (event) => {
                event.stopPropagation();
                const bpmnFactory = injector.get('bpmnFactory');
                const commands = [];
                const businessObject = getBusinessObject(element);
                let extensionElements = businessObject.extensionElements;

                // (1) ensure extension elements
                if (!extensionElements) {
                    extensionElements = createElement(
                        bpmnFactory,
                        'bpmn:ExtensionElements',
                        { values: [] },
                        businessObject
                    );

                    commands.push({
                        cmd: 'element.updateModdleProperties',
                        context: {
                            element,
                            moddleElement: businessObject,
                            properties: { extensionElements },
                        },
                    });
                }

                // (2) ensure actions extension
                let extension = getExtensionElementsList(element, 'fism:ListActions')[0];

                if (!extension) {
                    extension = createElement(
                        bpmnFactory,
                        'fism:ListActions',
                        { values: [] },
                        extensionElements
                    );

                    commands.push({
                        cmd: 'element.updateModdleProperties',
                        context: {
                            element,
                            moddleElement: extensionElements,
                            properties: {
                                values: [...extensionElements.get('values'), extension],
                            },
                        },
                    });
                }

                // (3) create and add action extension
                commands.push({
                    cmd: 'element.updateModdleProperties',
                    context: {
                        element,
                        moddleElement: extension,
                        properties: {
                            values: [
                                ...extension.get('values'),
                                createElement(
                                    bpmnFactory,
                                    'fism:ListAction',
                                    {
                                        id: injector.get('moddle').ids.nextPrefixed('ListAction_'),
                                        executeOn: 'entering',
                                    },
                                    extension
                                ),
                            ],
                        },
                    },
                });

                injector
                    .get('commandStack')
                    .execute('properties-panel.multi-command-executor', commands);
            },
            element,
            id: groupId,
            items: (
                (getExtensionElementsList(element, 'fism:ListActions')[0]?.values as any[]) ?? []
            ).map<ListItemDefinition>((item) => {
                const id = `${element.id}-${item.id}`;
                return {
                    id,
                    entries: [
                        {
                            component: NameComp,
                            id: id + '-name',
                            isEdited: isTextFieldEntryEdited,
                            moddleElement: item,
                        },
                        {
                            component: ActionComp,
                            id: id + '-action',
                            isEdited: isSelectEntryEdited,
                            moddleElement: item,
                        },
                        {
                            component: ExecuteOnComp,
                            id: id + '-executeOn',
                            isEdited: isSelectEntryEdited,
                            moddleElement: item,
                        },
                        ...entriesForType(id, item),
                    ] as any,
                    label:
                        item.action || item.name
                            ? `${translate(mapTypeToLabel(item.action) || '<empty>')}: ${item.name || translate('<empty>')}`
                            : translate('<empty>'),
                    remove: (event) => {
                        event.stopPropagation();
                        const extension = getExtensionElementsList(element, 'fism:ListActions')[0];
                        /*injector
                            .get('modeling')
                            .updateModdleProperties(element, extension, {
                                values: without(extension.values, item),
                            });*/

                        let values = without(extension.values, item);
                        const modeling = injector.get('modeling');
                        if (values.length > 0) {
                            modeling.updateModdleProperties(element, extension, { values });
                        } else {
                            const businessObject = getBusinessObject(element);
                            const extensionElements = businessObject.extensionElements;
                            values = without(extensionElements.values, extension);
                            if (values.length > 0) {
                                modeling.updateModdleProperties(element, extensionElements, {
                                    values,
                                });
                            } else {
                                modeling.updateModdleProperties(element, businessObject, {
                                    extensionElements: undefined,
                                });
                            }
                        }
                    },
                    autoOpen: true,
                    autoFocusEntry: id + '-name',
                };
            }),
            label: translate('SharePoint actions'),
            component: ListGroup,
        };
    }
};

type SpGroupComp<E = {}> = EntryDefinition<{}, E>['component'];

const NameComp: SpGroupComp<TCompProps> = (props) => {
    const { element, moddleElement } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');

    return TextFieldEntry({
        debounce: useService('debounceInput'),
        element,
        getValue: () => moddleElement.name || '',
        id: props.id,
        label: translate('Name'),
        setValue: (value) => {
            modeling.updateModdleProperties(element, moddleElement, { name: value });
        },
    });
};

const VariableNameComp: SpGroupComp<TCompProps> = (props) => {
    const { element, moddleElement } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');

    return TextFieldEntry({
        debounce: useService('debounceInput'),
        element,
        getValue: () => moddleElement.variableName || '',
        id: props.id,
        label: translate('Target Ids'),
        setValue: (value) => {
            modeling.updateModdleProperties(element, moddleElement, { variableName: value });
        },
    });
};

const variableNameProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    component: VariableNameComp,
    id: idPrefix + '-variableName',
    isEdited: isTextFieldEntryEdited,
    moddleElement,
});

const mapTypeToLabel = (type?: string): string => (type ? 'spListEntry-' + type : '');
const mapExecuteTypeToLabel = (type?: string): string => (type ? 'spExecuteOn-' + type : '');

const ActionComp: SpGroupComp<TCompProps> = (props) => {
    const { element, moddleElement } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const value = moddleElement.action || '';

    const options = useMemo(
        () =>
            ['find', 'create', 'update', 'copy', 'move', 'delete', 'startProcess'].map((value) =>
                createOption(translate(mapTypeToLabel(value)), value)
            ),
        []
    );

    const tooltipNode = () => {
        return h('div', { className: 'bio-properties-panel-tooltip-content' }, [
            h('i', {}, 'Ermitteln - '),
            h('span', {}, 'Listenelemente in einer SharePoint Liste ermitteln'),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Erstellen - '),
            h('span', {}, 'Neues Listenelement in einer SharePoint Liste erstellen'),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Aktualisieren - '),
            h('span', {}, 'Listenelement in einer SharePoint Liste aktualisieren'),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Kopieren - '),
            h('span', {}, 'Listenelement in eine SharePoint Liste kopieren'),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Verschieben - '),
            h('span', {}, 'Listenelement in eine SharePoint Liste verschieben'),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Löschen - '),
            h('span', {}, 'Listenelement aus einer SharePoint Liste löschen'),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Prozess starten - '),
            h(
                'span',
                {},
                'Listenelement aus einer SharePoint Liste ermitteln und für jedes gefundene Element einen neuen Prozess starten'
            ),
        ]);
    };

    return SelectEntry({
        element,
        getOptions: useCallback(
            () =>
                value
                    ? options
                    : [createOption(translate('Choose from'), '', true)].concat(options),
            [options, value]
        ),
        getValue: () => value,
        id: props.id,
        label: translate('Action'),
        tooltip: tooltipNode() as any,
        setValue: (newValue) => {
            const moddleProps: Record<string, any> = {
                action: newValue,
                filter: undefined,
                item: undefined,
                source: undefined,
                sourceSubSiteId: undefined,
                sourceSubSiteName: undefined,
                sourceSubSiteUrl: undefined,
                sourceListId: undefined,
                sourceListName: undefined,
                target: undefined,
                targetSubSiteId: undefined,
                targetSubSiteName: undefined,
                targetSubSiteUrl: undefined,
                targetListId: undefined,
                targetListName: undefined,
                // executeOn: undefined,
                variableName: undefined,
                processDefinitionKey: undefined,
                processDefinitionName: undefined,
            };

            if (!['copy', 'move'].includes(newValue)) {
                moddleProps.includeVersions = undefined;
                moddleProps.numberOfVersions = undefined;
            }

            modeling.updateModdleProperties(element, moddleElement, moddleProps);
        },
    });
};

const ExecuteOnComp: SpGroupComp<TCompProps> = (props) => {
    const { element, moddleElement } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const value = moddleElement.executeOn || '';

    const options = useMemo(
        () =>
            ['entering', 'leaving'].map((value) =>
                createOption(translate(mapExecuteTypeToLabel(value)), value)
            ),
        []
    );

    const tooltipNode = () => {
        return h('div', { className: 'bio-properties-panel-tooltip-content' }, [
            h('i', {}, 'Task-Eintritt - '),
            h('span', {}, 'Ausführen der Aktion beim Eintritt in den Task'),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Task-Austritt - '),
            h('span', {}, 'Ausführen der Aktion beim Verlassen des Tasks'),
        ]);
    };

    return SelectEntry({
        element,
        // disabled: loading,
        // description: loading ? translate('Loading...') : undefined,
        getOptions: useCallback(
            () =>
                value
                    ? options
                    : [createOption(translate('Choose from'), '', true)].concat(options),
            [options, value]
        ),
        getValue: () => value,
        id: props.id,
        label: translate('ExecuteOn'),
        tooltip: tooltipNode() as any,
        setValue: (newValue) => {
            modeling.updateModdleProperties(element, moddleElement, {
                executeOn: newValue,
            });
        },
    });
};

type TEntryPropsFn<R = unknown> = (
    idPrefix: string,
    moddleElement: Record<string, any>
) => R extends Array<any> ? Record<string, any>[] : Record<string, any>; // any extends Array<infer R> ? R[] : ;

const entriesForType: TEntryPropsFn<[]> = (idPrefix, moddleElement) => {
    switch (moddleElement.action) {
        case 'create':
            return [
                targetProps(idPrefix, moddleElement),
                ...(moddleElement.target === 'list'
                    ? [
                          targetListProps(idPrefix, moddleElement),
                          listItemProps(idPrefix, moddleElement),
                          variableNameProps(idPrefix, moddleElement),
                      ]
                    : moddleElement.target === 'subsite'
                      ? [
                            targetSubSiteProps(idPrefix, moddleElement),
                            ...(moddleElement.targetSubSiteId
                                ? [
                                      targetListProps(idPrefix, moddleElement),
                                      listItemProps(idPrefix, moddleElement),
                                      variableNameProps(idPrefix, moddleElement),
                                  ]
                                : []),
                        ]
                      : []),
            ];
        case 'update':
            return [
                targetProps(idPrefix, moddleElement),
                ...(moddleElement.target === 'list'
                    ? [
                          targetListProps(idPrefix, moddleElement),
                          listFilterProps(idPrefix, moddleElement),
                          listExpandProps(idPrefix, moddleElement),
                          listItemProps(idPrefix, moddleElement),
                      ]
                    : moddleElement.target === 'subsite'
                      ? [
                            targetSubSiteProps(idPrefix, moddleElement),
                            ...(moddleElement.targetSubSiteId
                                ? [
                                      targetListProps(idPrefix, moddleElement),
                                      listFilterProps(idPrefix, moddleElement),
                                      listExpandProps(idPrefix, moddleElement),
                                      listItemProps(idPrefix, moddleElement),
                                  ]
                                : []),
                        ]
                      : []),
            ];
        case 'find':
            return [
                sourceProps(idPrefix, moddleElement),
                ...(moddleElement.source === 'list'
                    ? [
                          sourceListProps(idPrefix, moddleElement),
                          listFilterProps(idPrefix, moddleElement),
                          listSelectProps(idPrefix, moddleElement),
                          listExpandProps(idPrefix, moddleElement),
                          listFieldProps(idPrefix, moddleElement),
                      ]
                    : moddleElement.source === 'current-list'
                      ? [
                            listFilterProps(idPrefix, moddleElement),
                            listSelectProps(idPrefix, moddleElement),
                            listExpandProps(idPrefix, moddleElement),
                            listFieldProps(idPrefix, moddleElement),
                        ]
                      : moddleElement.source === 'subsite'
                        ? [
                              sourceSubSiteProps(idPrefix, moddleElement),
                              ...(moddleElement.sourceSubSiteId
                                  ? [
                                        sourceListProps(idPrefix, moddleElement),
                                        listFilterProps(idPrefix, moddleElement),
                                        listSelectProps(idPrefix, moddleElement),
                                        listExpandProps(idPrefix, moddleElement),
                                        listFieldProps(idPrefix, moddleElement),
                                    ]
                                  : []),
                          ]
                        : []),
                variableNameProps(idPrefix, moddleElement),
            ].filter((entry) => !!entry);
        case 'copy':
        case 'move':
            return [
                sourceProps(idPrefix, moddleElement),
                ...(moddleElement.source === 'list'
                    ? [
                          sourceListProps(idPrefix, moddleElement),
                          listFilterProps(idPrefix, moddleElement),
                          listExpandProps(idPrefix, moddleElement),
                      ]
                    : moddleElement.source === 'current-list'
                      ? [
                            listFilterProps(idPrefix, moddleElement),
                            listExpandProps(idPrefix, moddleElement),
                        ]
                      : []),
                targetProps(idPrefix, moddleElement),
                ...(moddleElement.target === 'list'
                    ? [
                          targetListProps(idPrefix, moddleElement),
                          variableNameProps(idPrefix, moddleElement),
                      ]
                    : moddleElement.target === 'subsite'
                      ? [
                            targetSubSiteProps(idPrefix, moddleElement),
                            ...(moddleElement.targetSubSiteId
                                ? [
                                      targetListProps(idPrefix, moddleElement),
                                      variableNameProps(idPrefix, moddleElement),
                                  ]
                                : []),
                        ]
                      : []),

                moddleElement.includeVersions
                    ? numberOfVersionsProps(idPrefix, moddleElement)
                    : undefined,
            ].filter((entry) => !!entry);
        case 'delete':
            return [
                sourceProps(idPrefix, moddleElement),
                ...(moddleElement.source === 'list'
                    ? [
                          sourceListProps(idPrefix, moddleElement),
                          listFilterProps(idPrefix, moddleElement),
                          listExpandProps(idPrefix, moddleElement),
                      ]
                    : moddleElement.source === 'current-list'
                      ? [
                            listFilterProps(idPrefix, moddleElement),
                            listExpandProps(idPrefix, moddleElement),
                        ]
                      : moddleElement.source === 'subsite'
                        ? [
                              sourceSubSiteProps(idPrefix, moddleElement),
                              ...(moddleElement.sourceSubSiteId
                                  ? [
                                        sourceListProps(idPrefix, moddleElement),
                                        listFilterProps(idPrefix, moddleElement),
                                        listExpandProps(idPrefix, moddleElement),
                                    ]
                                  : []),
                          ]
                        : []),
            ];
        case 'startProcess':
            return [
                sourceProps(idPrefix, moddleElement),
                ...(moddleElement.source === 'list'
                    ? [
                          sourceListProps(idPrefix, moddleElement),
                          listFilterProps(idPrefix, moddleElement),
                          listExpandProps(idPrefix, moddleElement),
                      ]
                    : moddleElement.source === 'current-list'
                      ? [
                            listFilterProps(idPrefix, moddleElement),
                            listExpandProps(idPrefix, moddleElement),
                        ]
                      : []),
                processListProps(idPrefix, moddleElement),
            ].filter((entry) => !!entry);
        default:
            return [];
    }
};

type TCompProps<E = {}> = ListItemDefinition['entries'][number] & E;

const sourceProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    component: SourceComp,
    id: idPrefix + '-source',
    isEdited: isSelectEntryEdited,
    moddleElement,
});

const SourceComp: SpGroupComp<TCompProps> = (props) => {
    const { element, moddleElement } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const value = moddleElement.source || '';
    const action = moddleElement.action;

    const options = useMemo(() => {
        let values: string[] = [];
        switch (action) {
            case 'find':
            case 'delete':
                values = ['current-list', 'list', 'subsite'];
                break;
            case 'copy':
                values = ['current', 'current-list', 'list'];
                break;
            case 'move':
            case 'startProcess':
                values = ['current-list', 'list'];
                break;
            default:
                values = [];
        }
        return values.map((value) => createOption(translate(mapTypeToLabel(value)), value));
    }, [action]);

    const tooltipNode = () => {
        return h('div', { className: 'bio-properties-panel-tooltip-content' }, [
            h('i', {}, 'Aktuelles Element - '),
            h(
                'span',
                {},
                'Ausführen auf dem Element, das den Task ausgelöst hat. (Nur bei normalem Start-Event)'
            ),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Aktuelle Liste - '),
            h(
                'span',
                {},
                'Ausführen auf der Liste, die für den Prozess definiert ist. (Nur bei normalem Start-Event)'
            ),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Liste auf aktueller Seite - '),
            h('span', {}, 'Auswählen einer Liste auf der aktuellen Seite'),
        ]);
    };

    return SelectEntry({
        element,
        // disabled: loading,
        // description: loading ? translate('Loading...') : undefined,
        getOptions: useCallback(
            () =>
                value
                    ? options
                    : [createOption(translate('Choose from'), '', true)].concat(options),
            [options, value]
        ),
        getValue: () => value,
        id: props.id,
        label: translate('Source'),
        tooltip: tooltipNode() as any,
        setValue: (value) => {
            modeling.updateModdleProperties(element, moddleElement, {
                filter: undefined,
                source: value,
                sourceSubSiteId: undefined,
                sourceSubSiteName: undefined,
                sourceListId: undefined,
                sourceListName: undefined,
            });
        },
    });
};

const targetProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    component: TargetComp,
    id: idPrefix + '-target',
    isEdited: isSelectEntryEdited,
    moddleElement,
});

const TargetComp: SpGroupComp<TCompProps> = (props) => {
    const { element, moddleElement } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const value = moddleElement.target || '';
    const action = moddleElement.action;

    const options = useMemo(() => {
        let values: string[] = [];
        switch (action) {
            case 'create':
            case 'update':
            case 'copy':
            case 'move':
                values = ['list', 'subsite'];
                break;
            case 'startProcess':
                values = ['current-list', 'list'];
                break;
            default:
                values = [];
        }
        return values.map((value) => createOption(translate(mapTypeToLabel(value)), value));
    }, [action]);

    const tooltipNode = () => {
        return h('div', { className: 'bio-properties-panel-tooltip-content' }, [
            h('i', {}, 'Aktuelles Element - '),
            h(
                'span',
                {},
                'Ausführen auf dem Element, das den Task ausgelöst hat. (Nur bei normalem Start-Event)'
            ),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Aktuelle Liste - '),
            h(
                'span',
                {},
                'Ausführen auf der Liste, die für den Prozess definiert ist. (Nur bei normalem Start-Event)'
            ),
            h('br', {}),
            h('br', {}),
            h('i', {}, 'Liste auf aktueller Seite - '),
            h('span', {}, 'Auswählen einer Liste auf der aktuellen Seite'),
        ]);
    };

    return SelectEntry({
        element,
        // disabled: loading,
        // description: loading ? translate('Loading...') : undefined,
        getOptions: useCallback(
            () =>
                value
                    ? options
                    : [createOption(translate('Choose from'), '', true)].concat(options),
            [options, value]
        ),
        getValue: () => value,
        id: props.id,
        label: translate('Target'),
        tooltip: tooltipNode() as any,
        setValue: (value) => {
            modeling.updateModdleProperties(element, moddleElement, {
                //filter: undefined,
                target: value,
                targetSubSiteId: undefined,
                targetSubSiteName: undefined,
                targetListId: undefined,
                targetListName: undefined,
            });
        },
    });
};

const processListProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    component: SelectProcessComp,
    id: idPrefix + '-processList',
    isEdited: isSelectEntryEdited,
    label: 'Process list',
    moddleElement,
    valPropId: 'processDefinitionKey',
    valPropName: 'processDefinitionName',
});

const sourceSubSiteProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    component: SelectSubSiteComp,
    id: idPrefix + '-subSite',
    isEdited: isSelectEntryEdited,
    label: 'SubSite',
    moddleElement,
    valPropId: 'sourceSubSiteId',
    valPropName: 'sourceSubSiteName',
});

const sourceListProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    component: SelectListComp,
    id: idPrefix + '-sourceList',
    isEdited: isSelectEntryEdited,
    label: 'Source list',
    moddleElement,
    valPropId: 'sourceListId',
    valPropName: 'sourceListName',
});

const targetSubSiteProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    component: SelectSubSiteComp,
    id: idPrefix + '-subSite',
    isEdited: isSelectEntryEdited,
    label: 'SubSite',
    moddleElement,
    valPropId: 'targetSubSiteId',
    valPropName: 'targetSubSiteName',
});

const targetListProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    component: SelectListComp,
    id: idPrefix + '-targetList',
    isEdited: isSelectEntryEdited,
    label: 'Target list',
    moddleElement,
    valPropId: 'targetListId',
    valPropName: 'targetListName',
});

const listFilterProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    attrPropName: 'filter',
    component: TextWithFieldRef,
    id: idPrefix + '-filter',
    isEdited: isTextFieldEntryEdited,
    label: 'List filter',
    moddleElement,
    type: 'fism:ListFilter',
    valPropName: 'value',
    multiline: true,
    numberOfLines: 2,
});

const listSelectProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    attrPropName: 'select',
    component: JsonComp,
    id: idPrefix + '-select',
    isEdited: isTextFieldEntryEdited,
    label: 'List select',
    moddleElement,
    type: 'fism:ListSelect',
    valPropName: 'value',
    numberOfLines: 2,
});

const listExpandProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    attrPropName: 'expand',
    component: JsonComp,
    id: idPrefix + '-expand',
    isEdited: isTextFieldEntryEdited,
    label: 'List expand',
    moddleElement,
    type: 'fism:ListExpand',
    valPropName: 'value',
    numberOfLines: 2,
});

const listFieldProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    attrPropName: 'field',
    component: JsonComp,
    id: idPrefix + '-field',
    isEdited: isTextFieldEntryEdited,
    label: 'List field',
    moddleElement,
    type: 'fism:ListField',
    valPropName: 'value',
});

const listItemProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    attrPropName: 'item',
    component: TextWithFieldRef, //JsonComp,
    id: idPrefix + '-item',
    isEdited: isTextFieldEntryEdited,
    label: 'List item',
    moddleElement,
    type: 'fism:ListItem',
    valPropName: 'value',
    multiline: true,
});

interface ISelectListCompProps extends TCompProps, Record<string, any> {
    label: string;
    valPropId: string;
    valPropName: string;
}

const SelectSubSiteComp: SpGroupComp<ISelectListCompProps> = (props) => {
    const { element, moddleElement, valPropId, valPropName } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const siteUrl: string | undefined =
        useService('canvas').getRootElement().businessObject.siteUrl;
    const value = moddleElement[valPropId] || '';
    const { data, loading } = useFetchSpSubSites({ enabled: true, siteUrl });
    const options = useMemo(
        () => (data ? data.map((entry) => createOption(entry.name, entry.siteUrl!)) : []),
        [data]
    );

    return SelectEntry({
        element,
        description: loading
            ? translate('Loading...')
            : !siteUrl
              ? translate(
                    'Please first select a site in the process in the group "SharePoint list".'
                )
              : undefined,
        disabled: loading,
        getOptions: useCallback(() => {
            if (value) {
                const option = getOption(options, value);
                return option
                    ? options
                    : [createOption(moddleElement[valPropName], value)].concat(options);
            }

            return [createOption(translate('Choose from'), '', true)].concat(options);
        }, [options, value]),
        getValue: () => value,
        id: props.id,
        label: translate(props.label),
        setValue: (value) => {
            modeling.updateModdleProperties(element, moddleElement, {
                [valPropId]: value,
                [valPropName]: getOptionLabel(options, value),
            });
        },
    });
};

const SelectListComp: SpGroupComp<ISelectListCompProps> = (props) => {
    const { element, moddleElement, valPropId, valPropName } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const siteUrl: string | undefined =
        useService('canvas').getRootElement().businessObject.siteUrl;
    //const subSiteUrl: string | undefined = moddleElement[valPropId] || undefined;
    const subSiteUrl: string | undefined =
        (valPropId.includes('sourceList')
            ? moddleElement.sourceSubSiteId
            : moddleElement.targetSubSiteId) || undefined;

    const effectiveSiteUrl: string | undefined = subSiteUrl ?? siteUrl;
    const value = moddleElement[valPropId] || '';
    const { data, loading } = useFetchSpSiteLists({
        enabled: !!effectiveSiteUrl,
        siteUrl: effectiveSiteUrl,
    });
    const options = useMemo(
        () => (data ? data.map((entry) => createOption(entry.name, entry.id)) : []),
        [data]
    );

    return SelectEntry({
        element,
        description: loading
            ? translate('Loading...')
            : !effectiveSiteUrl
              ? translate(
                    'Please first select a site in the process in the group "SharePoint list".'
                )
              : undefined,
        disabled: loading,
        getOptions: useCallback(() => {
            if (value) {
                const option = getOption(options, value);
                return option
                    ? options
                    : [createOption(moddleElement[valPropName], value)].concat(options);
            }

            return [createOption(translate('Choose from'), '', true)].concat(options);
        }, [options, value]),
        getValue: () => value,
        id: props.id,
        label: translate(props.label),
        setValue: (value) => {
            modeling.updateModdleProperties(element, moddleElement, {
                [valPropId]: value,
                [valPropName]: getOptionLabel(options, value),
            });
        },
    });
};

const SelectProcessComp: SpGroupComp<ISelectListCompProps> = (props) => {
    const { element, moddleElement, valPropId, valPropName } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const siteUrl: string | undefined =
        useService('canvas').getRootElement().businessObject.siteUrl;
    const definitionKey: string | undefined = useService('canvas').getRootElement().id;
    const value = moddleElement[valPropId] || '';
    const { data, loading } = useFetchActiveProcesses({ enabled: true, siteUrl, definitionKey });
    const options = useMemo(
        () =>
            data
                ? data.map((entry) => createOption(entry.definitionName, entry.definitionKey))
                : [],
        [data]
    );

    const tooltipNode = () => {
        return h('div', { className: 'bio-properties-panel-tooltip-content' }, [
            h(
                'span',
                {},
                'Wählen Sie einen Prozess. Es wird für jedes Listenelement, das die Filterkriterien erfüllt, ein neuer Prozessinstanz dieses Prozesses gestartet.'
            ),
        ]);
    };

    return SelectEntry({
        element,
        description: loading
            ? translate('Loading...')
            : !siteUrl
              ? translate(
                    'Please first select a site in the process in the group "SharePoint list".'
                )
              : undefined,
        disabled: loading,
        getOptions: useCallback(() => {
            if (value) {
                const option = getOption(options, value);
                return option
                    ? options
                    : [createOption(moddleElement[valPropName], value)].concat(options);
            }

            return [createOption(translate('Choose from'), '', true)].concat(options);
        }, [options, value]),
        getValue: () => value,
        id: props.id,
        label: translate(props.label),
        tooltip: tooltipNode() as any,
        setValue: (value) => {
            modeling.updateModdleProperties(element, moddleElement, {
                [valPropId]: value,
                [valPropName]: getOptionLabel(options, value),
            });
        },
    });
};

interface IJsonCompProps extends TCompProps, Record<string, any> {
    attrPropName: string;
    label: string;
    type: string;
    valPropName: string;
    numberOfLines?: number;
}

const JsonComp: SpGroupComp<IJsonCompProps> = (props) => {
    const { element, moddleElement, id } = props;
    const bpmnFactory = useService('bpmnFactory');
    const modeling = useService('modeling');
    const translate = useService('translate');

    const tooltipFilter = () => {
        return id.includes('filter')
            ? h('div', { className: 'bio-properties-panel-tooltip-content' }, [
                  h(
                      'span',
                      {},
                      'Filter zum Einschränken der betroffenen Listenelemente. Es wird ein ODATA-Filter erwartet, z.B. "Title eq \'Test\'"',
                      "Es können Variablen verwendet werden, also bspw.: ID eq '{{Id}}'"
                  ),
                  h('br', {}),
                  h(
                      'span',
                      {},
                      'Rufen Sie die Hilfe auf für weitere Informationen und Beispiele zum Filtern von SharePoint-Listen.'
                  ),
              ])
            : id.includes('select')
              ? h('div', { className: 'bio-properties-panel-tooltip-content' }, [
                    h(
                        'span',
                        {},
                        'Erweitern der zu lesenden Listenelemente. Es wird ein ODATA-Select erwartet, z.B. "Author/EMail"'
                    ),
                    h('br', {}),
                    h(
                        'span',
                        {},
                        'Rufen Sie die Hilfe auf für weitere Informationen und Beispiele zum Filtern von SharePoint-Listen.'
                    ),
                ])
              : id.includes('expand')
                ? h('div', { className: 'bio-properties-panel-tooltip-content' }, [
                      h(
                          'span',
                          {},
                          'Erweiterung von Listenfiltern. Es wird ein ODATA-Expand erwartet, wenn z.B. Der Filter auf "Author/Title" prüft, dann muss hier "Author" angegeben werden, damit der Filter korrekt funktioniert.'
                      ),
                      h('br', {}),
                      h(
                          'span',
                          {},
                          'Rufen Sie die Hilfe auf für weitere Informationen und Beispiele zum Filtern von SharePoint-Listen.'
                      ),
                  ])
                : id.includes('field')
                  ? h('div', { className: 'bio-properties-panel-tooltip-content' }, [
                        h(
                            'span',
                            {},
                            'Geben Sie die Listenspalte an, die Sie auslesen möchten. Achten Sie dabei darauf, den Internal Name der Spalte zu verwenden. Also bspw. bei "Freigebende Person" -> "Freigebende_x0020_Person"',
                            'Sie können auch Unterelemente ansprechen , also z.B. User.EMail (Single User Spalte) oder User.0.Email (Multi User Spalte).',
                            'Die so ermittelten Werte werden in einer Liste für jedes gefundene Listenelement gespeichert und können in weiteren Aktionen verwendet werden.'
                        ),
                        h('br', {}),
                        h(
                            'span',
                            {},
                            'Rufen Sie die Hilfe auf für weitere Informationen und Beispiele zum Filtern von SharePoint-Listen.'
                        ),
                    ])
                  : h('div', { className: 'bio-properties-panel-tooltip-content' }, [
                        h(
                            'span',
                            {},
                            'Es wird ein Json erwartet, das die Werte für die Aktion definiert. Die genaue Struktur hängt von der Aktion ab.'
                        ),
                        h('br', {}),
                        h(
                            'span',
                            {},
                            'Rufen Sie die Hilfe auf für weitere Informationen und Beispiele zum Aufbau des JSON Objekts.'
                        ),
                    ]);
    };

    return TextAreaEntry({
        // autoResize: true,
        debounce: useService('debounceInput'),
        // description: translate('JSON format'),
        element,
        getValue: () => (moddleElement[props.attrPropName] || {})[props.valPropName] || '',
        id: props.id,
        label: translate(props.label),
        tooltip: tooltipFilter() as any,
        rows: props.numberOfLines ? props.numberOfLines : 1,
        setValue: (value) => {
            if (value) {
                if (moddleElement[props.attrPropName]) {
                    // update
                    modeling.updateModdleProperties(element, moddleElement[props.attrPropName], {
                        [props.valPropName]: value,
                    });
                } else {
                    // create and update
                    modeling.updateModdleProperties(element, moddleElement, {
                        [props.attrPropName]: bpmnFactory.create(props.type, {
                            [props.valPropName]: value,
                        }),
                    });
                }
            } else if (moddleElement.filter) {
                // remove and update
                modeling.updateModdleProperties(element, moddleElement, {
                    [props.attrPropName]: undefined,
                });
            }
        },
    });
};

/*
const includeVersionsProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    component: IncludeVersionsComp,
    id: idPrefix + '-includeVersions',
    isEdited: isCheckboxEntryEdited,
    moddleElement,
});



const IncludeVersionsComp: SpGroupComp<TCompProps> = (props) => {
    const { element, moddleElement } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');

    return CheckboxEntry({
        element,
        getValue: () => moddleElement.includeVersions === true,
        id: props.id,
        label: translate('Including version history'),
        setValue: (value) => {
            modeling.updateModdleProperties(element, moddleElement, {
                includeVersions: value,
                numberOfVersions: value ? '0' : undefined,
            });
        },
    });
};
 */
const numberOfVersionsProps: TEntryPropsFn = (idPrefix, moddleElement) => ({
    component: NumberOfVersionsComp,
    id: idPrefix + '-numberOfVersions',
    isEdited: isTextFieldEntryEdited,
    moddleElement,
});

const NumberOfVersionsComp: SpGroupComp<TCompProps> = (props) => {
    const { element, moddleElement } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');

    // NumberFieldEntry
    return TextFieldEntry({
        debounce: useService('debounceInput'),
        description: 'Hinweis: 0 = alle Versionen',
        element,
        getValue: () => moddleElement.numberOfVersions || '',
        id: props.id,
        label: translate('Number of versions'),
        setValue: (value, validationError) => {
            if (!validationError) {
                modeling.updateModdleProperties(element, moddleElement, {
                    numberOfVersions: Number.parseInt(value.trim(), 0).toString(),
                });
            }
        },
        validate: useCallback((value: string) => {
            if (!/^\d+$/.test(value)) {
                return translate('Only positive numbers without decimal places are allowed');
            }
        }, []),
    });
};
